/**
 * CloudWatch Error Logger for BridgeFirst Frontend
 * Logs frontend errors to CloudWatch via Cognito Identity Pool (no IAM keys in browser)
 */

import { CloudWatchLogsClient, PutLogEventsCommand, CreateLogStreamCommand, DescribeLogStreamsCommand } from '@aws-sdk/client-cloudwatch-logs'
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers'

// Configuration from environment variables
const REGION = import.meta.env.VITE_AWS_REGION || 'us-east-1'
const LOG_GROUP_NAME = import.meta.env.VITE_LOG_GROUP_NAME || '/aws/frontend/bridgefirst'
const IDENTITY_POOL_ID = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID
const LOGGING_ENABLED = import.meta.env.VITE_CLOUDWATCH_LOGGING_ENABLED === 'true'

// Client and stream state
let logsClient = null
let logStreamName = null
let sequenceToken = null
let isInitialized = false
let initializationError = null

/**
 * Initialize the CloudWatch Logs client
 */
function initLogger() {
  if (isInitialized) return

  try {
    if (!LOGGING_ENABLED) {
      isInitialized = true
      return
    }

    if (!IDENTITY_POOL_ID) {
      console.warn('[ErrorLogger] VITE_COGNITO_IDENTITY_POOL_ID not configured, falling back to console logging')
      isInitialized = true
      return
    }

    // Use Cognito Identity Pool for temporary credentials — no IAM keys in the browser
    logsClient = new CloudWatchLogsClient({
      region: REGION,
      credentials: fromCognitoIdentityPool({
        clientConfig: { region: REGION },
        identityPoolId: IDENTITY_POOL_ID,
      }),
    })
    
    // Create unique log stream name
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
    const sessionId = Math.random().toString(36).substring(7)
    logStreamName = `session-${timestamp}-${sessionId}`
    
    // console.log('[ErrorLogger] Initialized with CloudWatch logging')
    // console.log(`[ErrorLogger] Log Group: ${LOG_GROUP_NAME}`)
    // console.log(`[ErrorLogger] Log Stream: ${logStreamName}`)
    // console.log(`[ErrorLogger] Region: ${REGION}`)
    
    isInitialized = true
  } catch (error) {
    console.error('[ErrorLogger] Failed to initialize:', error)
    initializationError = error
    isInitialized = true // Mark as initialized to prevent retry loops
  }
}

/**
 * Create log stream if it doesn't exist
 */
async function ensureLogStream() {
  if (!logsClient || !logStreamName) {
    return false
  }

  try {
    // Check if stream exists
    const describeCommand = new DescribeLogStreamsCommand({
      logGroupName: LOG_GROUP_NAME,
      logStreamNamePrefix: logStreamName,
    })
    
    const describeResponse = await logsClient.send(describeCommand)
    
    if (describeResponse.logStreams && describeResponse.logStreams.length > 0) {
      // Stream exists, get sequence token
      sequenceToken = describeResponse.logStreams[0].uploadSequenceToken
      return true
    }
    
    // Create new stream
    const createCommand = new CreateLogStreamCommand({
      logGroupName: LOG_GROUP_NAME,
      logStreamName: logStreamName,
    })
    
    await logsClient.send(createCommand)
    // console.log('[ErrorLogger] Created log stream:', logStreamName)
    return true
    
  } catch (error) {
    console.error('[ErrorLogger] Failed to ensure log stream:', error)
    return false
  }
}

/**
 * Send log event to CloudWatch
 */
async function sendToCloudWatch(errorData) {
  if (!logsClient || !logStreamName) {
    return false
  }

  try {
    // Ensure log stream exists
    const streamReady = await ensureLogStream()
    if (!streamReady) {
      return false
    }

    // Prepare log event
    const logEvent = {
      message: JSON.stringify(errorData),
      timestamp: errorData.timestamp,
    }

    // Send to CloudWatch
    const putCommand = new PutLogEventsCommand({
      logGroupName: LOG_GROUP_NAME,
      logStreamName: logStreamName,
      logEvents: [logEvent],
      sequenceToken: sequenceToken,
    })

    const response = await logsClient.send(putCommand)
    
    // Update sequence token for next call
    sequenceToken = response.nextSequenceToken
    
    return true
  } catch (error) {
    console.error('[ErrorLogger] Failed to send to CloudWatch:', error)
    return false
  }
}

/**
 * Log error to CloudWatch (and console)
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export async function logError(error, context = {}) {
  try {
    if (!isInitialized) {
      initLogger()
    }

    const errorData = {
      timestamp: Date.now(),
      errorType: error.name || 'Error',
      message: error.message || 'Unknown error',
      stackTrace: error.stack || '',
      severity: context.severity || 'Error',
      userId: context.userId || null,
      sessionId: logStreamName,
      userAgent: navigator.userAgent,
      url: window.location.href,
      isTimeout: error.isTimeout || false,
      metadata: context.metadata || {},
    }

    // Always log to console for development
    console.error('[ErrorLogger]', errorData)

    // Send to CloudWatch if enabled and configured
    if (LOGGING_ENABLED && logsClient) {
      const sent = await sendToCloudWatch(errorData)
      if (sent) {
        // console.log('[ErrorLogger] Sent to CloudWatch')
      }
    }
    
  } catch (logError) {
    // Don't let logging errors break the app
    console.error('[ErrorLogger] Failed to log error:', logError)
  }
}

/**
 * Set up global error handlers
 */
export function setupErrorLogging() {
  // console.log('[ErrorLogger] Setting up global error handlers')
  
  // Catch unhandled errors
  window.addEventListener('error', (event) => {
    logError(event.error || new Error(event.message), {
      severity: 'Error',
      metadata: {
        type: 'uncaughtError',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      }
    })
  })

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason))
    
    logError(error, {
      severity: 'Error',
      metadata: {
        type: 'unhandledRejection',
      }
    })
  })

  // Initialize logger
  initLogger()
}

/**
 * Log a custom error with context
 * @param {string} message - Error message
 * @param {Object} context - Additional context
 */
export function logCustomError(message, context = {}) {
  const error = new Error(message)
  logError(error, context)
}

/**
 * Log a warning
 * @param {string} message - Warning message
 * @param {Object} context - Additional context
 */
export function logWarning(message, context = {}) {
  const error = new Error(message)
  logError(error, { ...context, severity: 'Warning' })
}

/**
 * Log a critical error
 * @param {string} message - Error message
 * @param {Object} context - Additional context
 */
export function logCritical(message, context = {}) {
  const error = new Error(message)
  logError(error, { ...context, severity: 'Critical' })
}
